import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { QueryClientContext } from "@tanstack/react-query";
import { getUploadStatus, getUploadStatusByClientId, listUploads, UploadStatusError, type ParsedRecordsDetail, type SourceGroup } from "@/features/patient/records/api/dataPipelineApi";
import { getSyncJobStatus, listSyncJobs } from "@/features/patient/records/api/syncJobApi";
import type { DocumentPagesResponse, DocumentFileResponse } from "@/features/patient/records/api/documentPagesApi";
import type { ZipUploadEntry } from "@/features/patient/records/components/UploadHistoryList";
import { appendUploadPage, mapSyncJobToEntry, mapUploadStatusToEntry } from "@/features/patient/records/utils/uploadHistoryPaging";
import { UPLOAD_STATUS_POLL_MS, POLL_BACKOFF_MAX_MS } from "@/features/patient/records/utils/pollingConfig";
import {
  isLiveChange,
  isRemoval,
  LIVE_BACKUP_POLL_MS,
  SYNC_CHANGED_EVENT,
  UPLOAD_CHANGED_EVENT,
  type LiveChange,
  type UploadSignal,
} from "@/features/patient/records/utils/liveUploadEvents";
import { getSocket } from "@/lib/socketClient";
import { onResync } from "@/lib/socketRecovery";
import { mocksEnabled } from "@/portals/clinician/api/transport";
import { uploadIdentityQuery } from "@/portals/patient/api/patientQueries";
import { startBackoffPolling, type BackoffPollerHandle } from "@/shared/lib/pollingBackoff";
import { usePatientAuthStore } from "@/stores/patientAuthStore";
import { useFamily } from "./FamilyContext";

// Split out of PatientDataContext: zip-upload status polls every few seconds while a
// zip is processing, and that used to force a re-render of every usePatientData()
// consumer (Dashboard, Timeline, Ask, RecordDetails) even though only the Upload page
// reads any of this. This context is scoped to just those readers.
interface UploadHistoryContextValue {
  zipUploads: ZipUploadEntry[];
  // True until the initial listUploads() call resolves (success or failure).
  // Without this, `zipUploads` reads as an empty array indistinguishable from
  // "patient truly has zero uploads" during that window — callers deciding
  // whether to show an empty state (e.g. DashboardPage) need to know the
  // difference so they don't flash the empty state for a patient who actually
  // has real, already-processed uploads.
  isLoading: boolean;
  addZipUpload: (entry: ZipUploadEntry) => void;
  updateZipEntry: (id: string, patch: Partial<ZipUploadEntry>) => void;
  /** Drops an upload or sync from the list at once (e.g. the patient deleted it), by its server id. */
  removeUpload: (uploadId: string) => void;
  /** actingAsProfileId: pass the Dedicated Profile's id this specific upload
   * belongs to (omit for "myself") — must match whatever was passed to
   * uploadMedicalZip for this upload, so polling checks status under the
   * right identity. Only needs calling directly for an upload whose real id
   * is already known (e.g. resuming one found in history) — for a freshly
   * submitted upload, the effect that starts this automatically once
   * `uploadId` appears on a "processing" entry (see below) is what actually
   * starts it in the normal case; this is still exported because that
   * effect calls it too, and recoverUploadId's caller may want it directly
   * once recovery finds a match. */
  pollUploadStatus: (localId: string, uploadId: string, actingAsProfileId?: string) => void;
  /** Same idea as pollUploadStatus, for a direct-API sync job entry
   *  (entry.kind === "sync") — polls getSyncJobStatus instead of
   *  getUploadStatus. Started automatically by the same dispatcher effect
   *  that starts pollUploadStatus, branching on entry.kind; exported mainly
   *  for parity/testability, not because callers normally need it directly. */
  pollSyncJobStatus: (localId: string, syncJobId: string, actingAsProfileId?: string) => void;
  /**
   * Called when uploadMedicalZip's response is lost (dropped connection,
   * tunnel reset, timeout) rather than rejected by the server — meaning the
   * upload may have genuinely succeeded server-side even though this client
   * never got a clean answer telling it the real upload_id. Looks it up by
   * the exact clientRequestId token that was passed to uploadMedicalZip for
   * this same attempt (see getUploadStatusByClientId) via a few bounded,
   * spaced-out calls; on a match, sets the entry's uploadId (status polling
   * then starts on its own — see above). Gives up and marks the entry
   * failed, with a message distinct from a real rejection, if no match
   * turns up — this must never poll forever for an upload that genuinely
   * never reached the server. */
  recoverUploadId: (localId: string, clientRequestId: string, actingAsProfileId?: string) => void;
  /** True if the server has more upload history past what's currently in
   *  `zipUploads` — drives whether "Your Uploads" shows a Load more control. */
  hasMoreUploads: boolean;
  /** True while a loadMoreUploads() fetch is in flight — disables the Load
   *  more control so a fast double-click can't request the same page twice. */
  isLoadingMoreUploads: boolean;
  /** Fetches the next page of upload history and appends it to `zipUploads`.
   *  No-ops if a fetch is already in flight or the server reported no more
   *  pages remain. */
  loadMoreUploads: () => void;
  /**
   * In-memory (never persisted — see this pair's own docstring below)
   * last-known ParsedRecordsDetail for one (uploadId, sourceGroup) pair, so
   * UploadDetailPanel can redisplay it instantly on revisit — switching back
   * to an already-viewed upload or tab — instead of blanking to a spinner
   * and refetching from scratch every time. Undefined means "never fetched
   * this session."
   *
   * `isFinal` matters: it's true only when this snapshot was fetched while
   * the upload was NOT "processing". A snapshot fetched WHILE processing can
   * predate data that only appears once conversion finishes (e.g. a PDF's
   * page-conversion committing its FHIR records) — the live-poll and the
   * separate upload-status poll run on independent timers, so the upload can
   * flip to "complete" moments after a poll tick caught an incomplete
   * snapshot. Trusting any cached snapshot just because the upload is now
   * terminal was exactly this bug: it could permanently freeze an empty/
   * partial "View PDF Data" tab even though the real data existed server-side.
   * Only a snapshot fetched with a non-"processing" status is guaranteed to
   * reflect the finished result, so only THAT is safe to treat as never-stale
   * and skip re-fetching for on a later visit — see UploadDetailPanel.
   */
  getCachedRecords: (uploadId: string, sourceGroup: SourceGroup) => { detail: ParsedRecordsDetail; isFinal: boolean } | undefined;
  cacheRecords: (uploadId: string, sourceGroup: SourceGroup, detail: ParsedRecordsDetail, isFinal: boolean) => void;
  /** Same idea as getCachedRecords/cacheRecords, for one (documentReferenceId,
   *  page) pair of ExtractedTextPanel's paginated page-level extraction view. */
  getCachedDocumentPages: (documentReferenceId: string, page: number) => DocumentPagesResponse | undefined;
  cacheDocumentPages: (documentReferenceId: string, page: number, response: DocumentPagesResponse) => void;
  /** Same idea again, for one document's getDocumentFile response (the
   *  presigned-URL bundle the "View Original File" tab renders). Lets a
   *  revisit reuse the exact same S3 URLs instead of minting new ones —
   *  which matters because a new presigned URL is a new resource to the
   *  browser, so without this the whole file (often multi-MB) is
   *  re-downloaded every time. The getter returns undefined once the entry
   *  is older than DOCUMENT_FILE_CACHE_TTL_MS — a stale entry would hand
   *  back a URL that's about to 403 mid-render. Never persisted (PHI), same
   *  as the two caches above. */
  getCachedDocumentFile: (documentReferenceId: string) => DocumentFileResponse | undefined;
  cacheDocumentFile: (documentReferenceId: string, response: DocumentFileResponse) => void;
  /** Ids (of `zipUploads` entries) that should currently show a bottom-right
   *  toast card — see UploadToastStack. Only ever populated by addZipUpload
   *  (a patient submitting a new upload just now), never by the history
   *  hydration below — a page reload should NOT replay toasts for uploads
   *  that were already in flight before it, only the "Your Uploads" list
   *  itself needs to resume tracking those. Order is oldest-first; newest
   *  upload's toast renders last (on top of the stack). */
  toastUploadIds: string[];
  /** Removes one id from toastUploadIds — called once a toast card's own
   *  exit animation finishes (auto, after it reaches a terminal status; or
   *  immediately, if the patient closes it by hand). Does not touch the
   *  underlying zipUploads entry at all, just the toast layer. */
  dismissToast: (id: string) => void;
  /** Tells `listener` about this upload: "changed" when the server just
   *  announced a change to it (live), "offline" when live messages stopped
   *  (socket dropped) so the caller should poll at its normal pace again.
   *  Returns the unsubscribe. Lets an open upload panel refresh the moment
   *  new data lands instead of only on its own timer. */
  onUploadChanged: (uploadId: string, listener: (signal: UploadSignal) => void) => () => void;
}

function recordsCacheKey(uploadId: string, sourceGroup: SourceGroup): string {
  return `${uploadId}:${sourceGroup}`;
}

function documentPagesCacheKey(documentReferenceId: string, page: number): string {
  return `${documentReferenceId}:${page}`;
}

// A DocumentFileResponse holds presigned S3 URLs valid for the backend's
// IMAGE_PRESIGNED_URL_EXPIRY_SECONDS (default 300s). Cache them for
// comfortably less than that so a cache hit never serves a URL that 403s
// part-way through rendering; a miss just re-fetches, which is cheap.
const DOCUMENT_FILE_CACHE_TTL_MS = 4 * 60 * 1000;

// Distinct from any real actingAsProfileId (string | undefined, undefined
// meaning "myself") — marks "the hydration effect has never run for this
// provider instance yet", so a genuine first run can be told apart from a
// later run that happens to see the same actingAsProfileId again. See the
// hydration effect below for why this is compared BY VALUE, not by an
// execution-count/"is this the first call" ref.
const PROFILE_ID_NEVER_HYDRATED = Symbol("profile-id-never-hydrated");

const UploadHistoryContext = createContext<UploadHistoryContextValue | null>(null);

export function UploadHistoryProvider({ children }: { children: ReactNode }) {
  // Re-fetches whenever the patient switches which profile they're viewing
  // (see FamilyContext.activeDependent) — this context's list is always
  // scoped to exactly one profile's uploads at a time, never merged.
  const { activeDependent } = useFamily();
  const actingAsProfileId = activeDependent?.id;

  const [zipUploads, setZipUploads] = useState<ZipUploadEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasMoreUploads, setHasMoreUploads] = useState(false);
  const [isLoadingMoreUploads, setIsLoadingMoreUploads] = useState(false);
  const pollingIds = useRef(new Set<string>());
  // Live updates (connection plan step 5): the running poller per upload or
  // sync id (so a live message can make it check at once), what open panels
  // listen for, and the list as of the last render (read by socket handlers).
  const pollersRef = useRef(new Map<string, BackoffPollerHandle>());
  const uploadListenersRef = useRef(new Map<string, Set<(signal: UploadSignal) => void>>());
  const zipUploadsRef = useRef<ZipUploadEntry[]>([]);
  zipUploadsRef.current = zipUploads;

  // Plain in-memory ref — deliberately never localStorage/sessionStorage/
  // IndexedDB, since this is PHI (parsed clinical records, extracted document
  // text). Living here means it's cleared automatically on logout (this whole
  // provider tree unmounts) and on switching acting-as profile below, exactly
  // like every other piece of state this context already holds — nothing extra
  // to remember to clean up.
  //
  // A ref, NOT useState: nothing here is ever read during render (only through
  // getCachedRecords/getCachedDocumentPages, both called from effects, not JSX),
  // so there's nothing a re-render would need to reflect. Making this useState
  // was a real regression: getCachedRecords was a useCallback keyed on that state,
  // so its identity changed on every single cache write — and UploadDetailPanel's
  // live-poll effect lists getCachedRecords as a dependency, so every poll tick's
  // own cacheRecords() call tore that effect down (clearing its interval and its
  // visibilitychange/focus listeners) and rebuilt it from scratch. A ref keeps
  // getCachedRecords/cacheRecords permanently stable, so that effect only ever
  // re-runs for the reasons it's actually supposed to.
  const recordsCacheRef = useRef<Record<string, { detail: ParsedRecordsDetail; isFinal: boolean }>>({});
  const documentPagesCacheRef = useRef<Record<string, DocumentPagesResponse>>({});
  const documentFileCacheRef = useRef<Record<string, { response: DocumentFileResponse; fetchedAt: number }>>({});
  const [toastUploadIds, setToastUploadIds] = useState<string[]>([]);

  const dismissToast = useCallback((id: string) => {
    setToastUploadIds((prev) => prev.filter((existing) => existing !== id));
  }, []);

  const getCachedRecords = useCallback(
    (uploadId: string, sourceGroup: SourceGroup) => recordsCacheRef.current[recordsCacheKey(uploadId, sourceGroup)],
    [],
  );
  const cacheRecords = useCallback((uploadId: string, sourceGroup: SourceGroup, detail: ParsedRecordsDetail, isFinal: boolean) => {
    recordsCacheRef.current = { ...recordsCacheRef.current, [recordsCacheKey(uploadId, sourceGroup)]: { detail, isFinal } };
  }, []);
  const getCachedDocumentPages = useCallback(
    (documentReferenceId: string, page: number) => documentPagesCacheRef.current[documentPagesCacheKey(documentReferenceId, page)],
    [],
  );
  const cacheDocumentPages = useCallback((documentReferenceId: string, page: number, response: DocumentPagesResponse) => {
    documentPagesCacheRef.current = { ...documentPagesCacheRef.current, [documentPagesCacheKey(documentReferenceId, page)]: response };
  }, []);
  const getCachedDocumentFile = useCallback((documentReferenceId: string) => {
    const entry = documentFileCacheRef.current[documentReferenceId];
    if (!entry || Date.now() - entry.fetchedAt > DOCUMENT_FILE_CACHE_TTL_MS) return undefined;
    return entry.response;
  }, []);
  const cacheDocumentFile = useCallback((documentReferenceId: string, response: DocumentFileResponse) => {
    documentFileCacheRef.current = { ...documentFileCacheRef.current, [documentReferenceId]: { response, fetchedAt: Date.now() } };
  }, []);
  // How many rows have been fetched FROM THE SERVER so far, in page order —
  // the offset the next loadMoreUploads() call uses. Deliberately separate
  // from zipUploads.length: a brand-new upload prepended by addZipUpload
  // (optimistic, not yet reflected server-side) must never shift what the
  // next server page request asks for.
  const fetchedCountRef = useRef(0);

  const updateZipEntry = useCallback((id: string, patch: Partial<ZipUploadEntry>) => {
    setZipUploads((prev) => prev.map((z) => (z.id === id ? { ...z, ...patch } : z)));
  }, []);

  const removeUpload = useCallback((uploadId: string) => {
    const gone = (z: ZipUploadEntry) => z.id === uploadId || z.uploadId === uploadId;
    setZipUploads((prev) => {
      const removedIds = new Set(prev.filter(gone).map((z) => z.id));
      if (removedIds.size === 0) return prev;
      setToastUploadIds((ids) => ids.filter((id) => !removedIds.has(id)));
      return prev.filter((z) => !gone(z));
    });
  }, []);

  const addZipUpload = useCallback((entry: ZipUploadEntry) => {
    setZipUploads((prev) => [entry, ...prev]);
    setToastUploadIds((prev) => [...prev, entry.id]);
  }, []);

  const pollUploadStatus = useCallback((localId: string, uploadId: string, pollActingAsProfileId?: string) => {
    if (pollingIds.current.has(uploadId)) return;
    pollingIds.current.add(uploadId);

    let onVisibilityChange: (() => void) | undefined;
    const stopWatchingVisibility = () => {
      if (!onVisibilityChange) return;
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", onVisibilityChange);
      onVisibilityChange = undefined;
    };

    // Backs off on consecutive failures instead of retrying a struggling
    // connection at full rate (see pollingBackoff.ts), and genuinely
    // suspends — zero network activity, not just "also fetches on return" —
    // while this tab is hidden. This poll previously had NO visibility
    // handling at all, unlike UploadDetailPanel's records poll; both now go
    // through the same mechanism.
    let poller: ReturnType<typeof startBackoffPolling>;
    poller = startBackoffPolling({
      intervalMs: UPLOAD_STATUS_POLL_MS,
      maxIntervalMs: POLL_BACKOFF_MAX_MS,
      poll: async () => {
        try {
          const result = await getUploadStatus(uploadId, pollActingAsProfileId);
          if (result.status === "processing") {
            // Still going — but not a no-op: surface the real stage/percent
            // from THIS tick so the progress bar actually moves while it
            // waits, instead of only ever updating once on the final,
            // terminal response.
            updateZipEntry(localId, {
              processingStage: result.processing_stage ?? undefined,
              processingProgress: result.processing_progress ?? undefined,
              processingOcrDone: result.processing_ocr_done ?? undefined,
              processingOcrTotal: result.processing_ocr_total ?? undefined,
            });
            return;
          }
          pollingIds.current.delete(uploadId);
          updateZipEntry(localId, {
            status: result.status,
            fileCount: result.file_count ?? undefined,
            errorMessage: result.error_message ?? undefined,
            files: result.files ?? undefined,
            parsingIssues: result.parsing_issues ?? undefined,
            processingSeconds: result.processing_seconds ?? undefined, // TEMP-TIMING
            processingStage: undefined,
            processingProgress: undefined,
          });
          poller.stop();
          pollersRef.current.delete(uploadId);
          stopWatchingVisibility();
        } catch (err) {
          if (err instanceof UploadStatusError && err.status === 404) {
            // Permanent — this upload doesn't exist (or isn't ours). Retrying
            // forever would be pointless. Resolves normally rather than
            // rethrowing: a 404 is a terminal signal, not a connectivity
            // failure, so it shouldn't trigger backoff on its way out.
            pollingIds.current.delete(uploadId);
            updateZipEntry(localId, { status: "failed", errorMessage: "We couldn't find this upload anymore." });
            poller.stop();
            pollersRef.current.delete(uploadId);
            stopWatchingVisibility();
            return;
          }
          // Transient network hiccup — rethrow so the poller backs off
          // before its next attempt instead of retrying at full rate.
          throw err;
        }
      },
    });

    pollersRef.current.set(uploadId, poller);
    if (document.visibilityState === "hidden") poller.pause();
    onVisibilityChange = () => {
      if (document.visibilityState === "visible") poller.resume();
      else poller.pause();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", onVisibilityChange);
  }, [updateZipEntry]);

  // Direct-API-sync counterpart to pollUploadStatus above — same
  // startBackoffPolling/visibility-pause plumbing, but against
  // getSyncJobStatus and with no per-stage weighting to surface mid-poll
  // (the backend already returns a flat completed/total percent, so there's
  // nothing to compute here beyond passing it through).
  const pollSyncJobStatus = useCallback((localId: string, syncJobId: string, pollActingAsProfileId?: string) => {
    if (pollingIds.current.has(syncJobId)) return;
    pollingIds.current.add(syncJobId);

    let onVisibilityChange: (() => void) | undefined;
    const stopWatchingVisibility = () => {
      if (!onVisibilityChange) return;
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", onVisibilityChange);
      onVisibilityChange = undefined;
    };

    let poller: ReturnType<typeof startBackoffPolling>;
    poller = startBackoffPolling({
      intervalMs: UPLOAD_STATUS_POLL_MS,
      maxIntervalMs: POLL_BACKOFF_MAX_MS,
      poll: async () => {
        try {
          const result = await getSyncJobStatus(syncJobId, pollActingAsProfileId);
          if (result.status === "processing") {
            updateZipEntry(localId, {
              processingStage: "processing",
              processingProgress: result.processing_progress ?? undefined,
            });
            return;
          }
          pollingIds.current.delete(syncJobId);
          updateZipEntry(localId, {
            status: result.status,
            errorMessage: result.error_message ?? undefined,
            processingStage: undefined,
            processingProgress: undefined,
            processingSeconds: result.processing_seconds ?? undefined,
          });
          poller.stop();
          pollersRef.current.delete(syncJobId);
          stopWatchingVisibility();
        } catch (err) {
          if (err instanceof UploadStatusError && err.status === 404) {
            pollingIds.current.delete(syncJobId);
            updateZipEntry(localId, { status: "failed", errorMessage: "We couldn't find this sync anymore." });
            poller.stop();
            pollersRef.current.delete(syncJobId);
            stopWatchingVisibility();
            return;
          }
          throw err;
        }
      },
    });

    pollersRef.current.set(syncJobId, poller);
    if (document.visibilityState === "hidden") poller.pause();
    onVisibilityChange = () => {
      if (document.visibilityState === "visible") poller.resume();
      else poller.pause();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", onVisibilityChange);
  }, [updateZipEntry]);

  // Starts status polling based on an entry existing in "processing" with a
  // known uploadId — not on the upload response itself having succeeded.
  // A lost upload response (dropped connection, tunnel reset, timeout) must
  // not prevent polling once the real id becomes known some other way (a
  // direct success response, or recoverUploadId finding a match below) —
  // this effect is the single place that starts a poller, so every path
  // that learns a real uploadId is covered the same way, instead of each
  // one needing its own explicit pollUploadStatus() call. pollUploadStatus's
  // own pollingIds guard makes calling it again for an id already being
  // polled a harmless no-op, so this can safely re-run on every zipUploads
  // change (which happens often — every poll tick updates it). Branches on
  // entry.kind so a sync entry polls getSyncJobStatus instead of
  // getUploadStatus — without this, a sync entry would poll the upload
  // endpoint against an id it has never heard of and 404 forever.
  useEffect(() => {
    for (const entry of zipUploads) {
      if (entry.status !== "processing" || !entry.uploadId) continue;
      if (entry.kind === "sync") {
        pollSyncJobStatus(entry.id, entry.uploadId, actingAsProfileId);
      } else {
        pollUploadStatus(entry.id, entry.uploadId, actingAsProfileId);
      }
    }
  }, [zipUploads, actingAsProfileId, pollUploadStatus, pollSyncJobStatus]);

  // Matches an optimistic entry back to its real server-side upload after
  // uploadMedicalZip's response is lost (see UploadRequestError.networkError
  // in dataPipelineApi.ts) — the upload may have genuinely succeeded even
  // though this client never got a clean answer telling it the real
  // upload_id. Recovering the id by listing recent uploads (rather than
  // just declaring the upload failed) is justified here: the alternative is
  // reporting a false "failed" for an upload the worker is actively
  // completing successfully, which is strictly worse than a few seconds of
  // uncertainty. Looks it up by the exact clientRequestId token sent with
  // the original upload (see dataPipelineApi.ts's getUploadStatusByClientId)
  // — an exact match, not a guess. This used to match by (file_name, recent
  // uploaded_at) instead, which could misattribute two uploads of an
  // identically-named file landing close together in time; a token can't
  // collide. Bounded at RECOVERY_MAX_ATTEMPTS rather than retrying forever
  // for an upload that genuinely never reached the server — a 404 here just
  // means "not found yet, or never", indistinguishable, and both are
  // handled the same way: keep trying, then give up.
  const recoverUploadId = useCallback((localId: string, clientRequestId: string, pollActingAsProfileId?: string) => {
    const RECOVERY_MAX_ATTEMPTS = 5;
    const RECOVERY_RETRY_MS = UPLOAD_STATUS_POLL_MS;
    let attempt = 0;

    const tryOnce = async () => {
      attempt += 1;
      try {
        const match = await getUploadStatusByClientId(clientRequestId, pollActingAsProfileId);
        updateZipEntry(localId, {
          uploadId: match.upload_id,
          status: match.status,
          fileCount: match.file_count ?? undefined,
          errorMessage: match.error_message ?? undefined,
          files: match.files ?? undefined,
          parsingIssues: match.parsing_issues ?? undefined,
          processingStage: match.processing_stage ?? undefined,
          processingProgress: match.processing_progress ?? undefined,
          processingOcrDone: match.processing_ocr_done ?? undefined,
          processingOcrTotal: match.processing_ocr_total ?? undefined,
        });
        // Status polling starts on its own via the effect above, now that
        // uploadId is set — no separate pollUploadStatus() call needed.
        return;
      } catch {
        // Not found yet (404) or a transient failure looking it up — either
        // way, fall through to the same retry/give-up handling below.
      }
      if (attempt < RECOVERY_MAX_ATTEMPTS) {
        setTimeout(tryOnce, RECOVERY_RETRY_MS);
      } else {
        updateZipEntry(localId, {
          status: "failed",
          errorMessage: "We couldn't confirm this upload went through. Please check \"Your Uploads\" in a moment, or try again.",
        });
      }
    };

    setTimeout(tryOnce, RECOVERY_RETRY_MS);
  }, [updateZipEntry]);

  // The actingAsProfileId this effect hydrated FOR, last time it ran —
  // starts at the sentinel above so a genuine first run is unambiguous even
  // though "myself" (undefined) is itself a legitimate, common
  // actingAsProfileId value.
  const previousProfileIdRef = useRef<string | typeof PROFILE_ID_NEVER_HYDRATED | undefined>(
    PROFILE_ID_NEVER_HYDRATED,
  );

  // Hydrate the currently-viewed profile's first page of upload history —
  // runs on mount AND every time actingAsProfileId changes (switching between
  // "myself" and a dependent) — and resumes polling anything still
  // mid-processing (e.g. they closed the tab, or switched profiles, before
  // the worker finished).
  useEffect(() => {
    let cancelled = false;
    // A REAL profile switch — the value actually changed since this effect
    // last ran — not just "is this literally the first time this effect
    // has ever executed." That distinction matters: React fires a CHILD's
    // mount effects before its ancestors', so on this provider's
    // first-ever mount (the one true page load the Epic OAuth callback
    // lands on, per useEpicConnectReturn.ts), a descendant page's effect
    // calling addZipUpload() to add an optimistic "Epic Sync" row can
    // already have run by the time this effect's body executes here.
    // Comparing VALUES (rather than an execution-count ref that just flips
    // once and stays flipped) also survives React StrictMode's dev-only
    // mount→cleanup→remount double-invoke of effects: a first-run ref would
    // read "already ran" on that synthetic second invocation even though
    // actingAsProfileId never actually changed, and wipe the very entry
    // this guard exists to protect. Unconditionally resetting to [] here —
    // previously done on every run, including the first — silently wiped
    // that row out before it ever rendered, with no error and no trace,
    // right as the patient landed back from Epic.
    const isRealProfileSwitch =
      previousProfileIdRef.current !== PROFILE_ID_NEVER_HYDRATED &&
      previousProfileIdRef.current !== actingAsProfileId;
    previousProfileIdRef.current = actingAsProfileId;

    setIsLoading(true);
    if (isRealProfileSwitch) {
      // A real profile switch still needs this reset exactly as before:
      // it's what stops the previous profile's list flashing while the new
      // one loads.
      setZipUploads([]); // clear the previous profile's list immediately — never show it while the new one loads
    }
    setHasMoreUploads(false);
    fetchedCountRef.current = 0;
    // All three caches are keyed by uploadId/documentReferenceId only, not
    // by profile — so a stale entry from the profile just switched away
    // from would otherwise leak into whichever profile is now active.
    recordsCacheRef.current = {};
    documentPagesCacheRef.current = {};
    documentFileCacheRef.current = {};
    // A toast belongs to whichever profile's upload triggered it — never
    // meaningful to carry one across a profile switch. Same guard as
    // setZipUploads above: addZipUpload sets both together, so skipping one
    // but not the other would still lose the sync entry's toast card on
    // that exact landing.
    if (isRealProfileSwitch) {
      setToastUploadIds([]);
    }

    listUploads(actingAsProfileId)
      .then((response) => {
        if (cancelled) return;
        const serverEntries = response.data.map(mapUploadStatusToEntry);
        // Merge, never blindly replace: this fetch can still be in flight when
        // the patient submits a brand-new upload — addZipUpload prepends an
        // optimistic entry (keyed by a local id the server doesn't know about
        // yet) while this promise is pending, and overwriting `zipUploads`
        // outright here would silently discard it the moment this resolves —
        // orphaning whatever UI had already selected it (stuck on "Select an
        // upload" with no way to recover, since its id no longer exists
        // anywhere in the list).
        //
        // A local entry the server ALSO already knows about (matched by
        // `.uploadId`, since an optimistic entry's `.id` stays its own
        // client-generated one forever — see UploadDetailPanel's docstring
        // on why it never changes) is kept AS-IS, not replaced by the
        // server's row for it. This used to replace it — silently swapping
        // in a stale one-time snapshot (the server row's `.id` is the real
        // upload id, a DIFFERENT value than the local entry's `.id`) the
        // moment this fetch resolved, discarding whatever pollUploadStatus
        // had already live-updated it to and orphaning that same poller
        // (still running, still calling updateZipEntry(localId, ...), but
        // now writing to an id that no longer exists in the list — a
        // visible "progress jumps back down, then looks stuck" regression,
        // worse the longer a sync/upload takes, since that's exactly how
        // much bigger this fetch's race window against it gets). Only a
        // server row with NO local counterpart at all — genuine history
        // this session never optimistically added — is appended.
        setZipUploads((prev) => {
          const localUploadIds = new Set(
            prev.map((p) => p.uploadId).filter((id): id is string => Boolean(id)),
          );
          const localIds = new Set(prev.map((p) => p.id));
          return [
            ...prev,
            ...serverEntries.filter((e) => !localUploadIds.has(e.id) && !localIds.has(e.id)),
          ];
        });
        fetchedCountRef.current = response.data.length;
        setHasMoreUploads(response.hasMore);
        // No explicit pollUploadStatus() call here — the effect below starts
        // a poller for any entry it finds in "processing" with a known
        // uploadId, which every row just merged in above already is.
      })
      .catch(() => {
        // History endpoint not available yet — fall back to session-only tracking.
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [actingAsProfileId]);

  // Hydrates this profile's direct-API sync history (Epic today) alongside
  // upload history — a separate, independent effect so a slow/failing
  // sync-jobs endpoint never blocks or fails upload-history hydration
  // (isLoading above is tied only to listUploads). Direct-API syncs are
  // infrequent, so no pagination/loadMore counterpart is needed here, unlike
  // uploads. Declared after the upload-hydration effect above so its
  // `setZipUploads([])` clear (on mount/profile-switch) always runs first —
  // React runs same-commit effects in declaration order — before this
  // effect's fetch can resolve and merge sync entries in.
  useEffect(() => {
    let cancelled = false;
    listSyncJobs(actingAsProfileId)
      .then((response) => {
        if (cancelled) return;
        const serverEntries = response.data.map(mapSyncJobToEntry);
        // Same merge-keep-local reasoning as the upload hydration effect
        // above: a sync just kicked off via addZipUpload (optimistic, local
        // id) may still be in flight when this resolves — this call happens
        // right around the same time useEpicConnectReturn.ts's redirect
        // lands, so the race is routinely hit here, not just theoretical.
        // Keeping the local entry (already being live-updated by
        // pollSyncJobStatus) instead of replacing it with this fetch's own
        // one-time snapshot under a different id is what stops the
        // "progress jumps back down / the row visibly blinks" bug — see the
        // upload effect's own comment for the full mechanics.
        setZipUploads((prev) => {
          const localUploadIds = new Set(
            prev.map((p) => p.uploadId).filter((id): id is string => Boolean(id)),
          );
          const localIds = new Set(prev.map((p) => p.id));
          return [
            ...prev,
            ...serverEntries.filter((e) => !localUploadIds.has(e.id) && !localIds.has(e.id)),
          ];
        });
        // No explicit pollSyncJobStatus() call here — the dispatcher effect
        // above starts a poller for any entry it finds in "processing" with
        // a known uploadId, which every row just merged in above already is.
      })
      .catch(() => {
        // Sync history endpoint not available/failed — upload history still works fine on its own.
      });
    return () => {
      cancelled = true;
    };
  }, [actingAsProfileId]);

  const onUploadChanged = useCallback((uploadId: string, listener: (signal: UploadSignal) => void) => {
    const listeners = uploadListenersRef.current.get(uploadId) ?? new Set<(signal: UploadSignal) => void>();
    listeners.add(listener);
    uploadListenersRef.current.set(uploadId, listeners);
    return () => {
      listeners.delete(listener);
      if (listeners.size === 0) uploadListenersRef.current.delete(uploadId);
    };
  }, []);

  // Live updates (connection plan step 5). The server announces every real
  // change to this profile's uploads and syncs (ids only). An upload being
  // polled checks at once, and once such a message has arrived for it its poll
  // only backs the messages up (LIVE_BACKUP_POLL_MS); if messages never arrive
  // (socket down, server without the change triggers) polling stays exactly as
  // before. Another device's new upload or sync is added, a deleted one leaves
  // the list, a finished one that changes again (e.g. its identity check) is
  // refreshed, and an open panel is told. After a reconnect everything checks
  // once, since messages sent meanwhile are gone.
  const queryClient = useContext(QueryClientContext);
  const selfProfileId = usePatientAuthStore((state) => state.user?.id);
  const viewedProfileId = actingAsProfileId ?? selfProfileId;

  useEffect(() => {
    if (mocksEnabled() || !viewedProfileId) return;
    const socket = getSocket();
    const pendingAdds = new Set<string>();
    let disposed = false;

    const known = (id: string) => zipUploadsRef.current.find((z) => z.uploadId === id || z.id === id);
    const tellPanels = (id: string, signal: UploadSignal) =>
      uploadListenersRef.current.get(id)?.forEach((listener) => listener(signal));

    const mergeNew = (entries: ZipUploadEntry[]) => {
      setZipUploads((prev) => {
        const present = new Set(prev.flatMap((p) => [p.id, p.uploadId]).filter(Boolean));
        const fresh = entries.filter((e) => !present.has(e.id));
        return fresh.length ? [...prev, ...fresh] : prev;
      });
    };

    // An upload this device is sending may not know its server id yet: wait
    // (up to 30s) for its own request to finish, so it is never listed twice.
    const addFromServer = (id: string, kind: "upload" | "sync", attempt = 0) => {
      if (disposed) return;
      if (known(id)) {
        pendingAdds.delete(id);
        return;
      }
      const sending = zipUploadsRef.current.some((z) => !z.uploadId && z.status !== "failed");
      if (sending && attempt < 30) {
        setTimeout(() => addFromServer(id, kind, attempt + 1), 1000);
        return;
      }
      pendingAdds.delete(id);
      const load = kind === "sync"
        ? listSyncJobs(actingAsProfileId).then((r) => r.data.filter((job) => job.sync_job_id === id).map(mapSyncJobToEntry))
        : getUploadStatus(id, actingAsProfileId).then((r) => [mapUploadStatusToEntry(r)]);
      load
        .then((entries) => {
          if (disposed || entries.length === 0) return;
          if (kind === "upload") fetchedCountRef.current += 1; // the server's pages moved down by one
          mergeNew(entries);
        })
        .catch(() => {
          // Best effort: the list still shows it after the next reload.
        });
    };

    // A row already finished here that changed again on the server.
    const refreshFinished = (entry: ZipUploadEntry, id: string) => {
      getUploadStatus(id, actingAsProfileId)
        .then((r) => {
          if (disposed) return;
          updateZipEntry(entry.id, {
            status: r.status,
            fileCount: r.file_count ?? undefined,
            errorMessage: r.error_message ?? undefined,
            files: r.files ?? undefined,
            parsingIssues: r.parsing_issues ?? undefined,
          });
        })
        .catch(() => {});
    };

    const onChange = (kind: "upload" | "sync") => (data: unknown) => {
      if (!isLiveChange(data) || data.profile !== viewedProfileId) return;
      const change: LiveChange = data;
      const { id } = change;

      if (kind === "upload" && isRemoval(change)) {
        pollersRef.current.get(id)?.stop();
        pollersRef.current.delete(id);
        pollingIds.current.delete(id);
        removeUpload(id);
        return;
      }

      tellPanels(id, "changed");
      if (kind === "upload" && queryClient && (change.kind === "upload_identity_checks" || change.kind === "upload_jobs")) {
        void queryClient.invalidateQueries({ queryKey: uploadIdentityQuery(id, actingAsProfileId).queryKey });
      }

      const poller = pollersRef.current.get(id);
      if (poller) {
        poller.setIntervalMs(LIVE_BACKUP_POLL_MS);
        // At most today's pace: page-by-page progress can announce a change every second.
        poller.pollNow(UPLOAD_STATUS_POLL_MS);
        return;
      }

      const entry = known(id);
      if (entry) {
        if (kind === "upload" && change.kind === "upload_jobs" && entry.status !== "processing") refreshFinished(entry, id);
        return;
      }

      const created = change.op === "INSERT" && (change.kind === "upload_jobs" || change.kind === "direct_api_sync_jobs");
      if (created && !pendingAdds.has(id)) {
        pendingAdds.add(id);
        addFromServer(id, kind);
      }
    };

    const onUpload = onChange("upload");
    const onSync = onChange("sync");

    // Socket gone: polls go back to their normal pace until messages are proven again.
    const onDisconnect = () => {
      pollersRef.current.forEach((poller) => poller.setIntervalMs(UPLOAD_STATUS_POLL_MS));
      uploadListenersRef.current.forEach((_listeners, id) => tellPanels(id, "offline"));
    };

    socket.on(UPLOAD_CHANGED_EVENT, onUpload);
    socket.on(SYNC_CHANGED_EVENT, onSync);
    socket.on("disconnect", onDisconnect);
    const offResync = onResync(socket, () => {
      pollersRef.current.forEach((poller) => poller.pollNow(UPLOAD_STATUS_POLL_MS));
      listUploads(actingAsProfileId)
        .then((r) => { if (!disposed) mergeNew(r.data.map(mapUploadStatusToEntry)); })
        .catch(() => {});
      listSyncJobs(actingAsProfileId)
        .then((r) => { if (!disposed) mergeNew(r.data.map(mapSyncJobToEntry)); })
        .catch(() => {});
    });

    return () => {
      disposed = true;
      socket.off(UPLOAD_CHANGED_EVENT, onUpload);
      socket.off(SYNC_CHANGED_EVENT, onSync);
      socket.off("disconnect", onDisconnect);
      offResync();
    };
  }, [viewedProfileId, actingAsProfileId, queryClient, removeUpload, updateZipEntry]);

  const loadMoreUploads = useCallback(() => {
    if (isLoadingMoreUploads || !hasMoreUploads) return;
    setIsLoadingMoreUploads(true);

    listUploads(actingAsProfileId, { offset: fetchedCountRef.current })
      .then((response) => {
        setZipUploads((prev) => appendUploadPage(prev, response.data));
        fetchedCountRef.current += response.data.length;
        setHasMoreUploads(response.hasMore);
        response.data
          .filter((h) => h.status === "processing")
          .forEach((h) => pollUploadStatus(h.upload_id, h.upload_id, actingAsProfileId));
      })
      .catch(() => {
        // Transient failure — leave hasMoreUploads as-is so the Load more
        // control stays visible and the patient can just try again.
      })
      .finally(() => setIsLoadingMoreUploads(false));
  }, [actingAsProfileId, hasMoreUploads, isLoadingMoreUploads, pollUploadStatus]);

  // zipUploads itself is left in whatever order its two independent source
  // effects (listUploads, listSyncJobs) happened to merge entries in — each
  // is individually newest-first from the backend, but merging two already-
  // sorted lists by concatenation doesn't interleave them by date (uploads
  // then syncs appended after, not true chronological order). Sorted only
  // here, for what's actually rendered — the raw state stays untouched for
  // the polling-dispatch effect above and the merge logic, neither of which
  // cares about order.
  const sortedZipUploads = useMemo(
    () => [...zipUploads].sort((a, b) => b.uploadedAt - a.uploadedAt),
    [zipUploads],
  );

  return (
    <UploadHistoryContext.Provider
      value={{
        zipUploads: sortedZipUploads, isLoading, addZipUpload, updateZipEntry, removeUpload, pollUploadStatus, pollSyncJobStatus, recoverUploadId,
        hasMoreUploads, isLoadingMoreUploads, loadMoreUploads,
        getCachedRecords, cacheRecords, getCachedDocumentPages, cacheDocumentPages,
        getCachedDocumentFile, cacheDocumentFile,
        toastUploadIds, dismissToast, onUploadChanged,
      }}
    >
      {children}
    </UploadHistoryContext.Provider>
  );
}

/** Same, or null outside the patient portal's provider (e.g. a component rendered on its own). */
export function useOptionalUploadHistory() {
  return useContext(UploadHistoryContext);
}

export function useUploadHistory() {
  const ctx = useContext(UploadHistoryContext);
  if (!ctx) throw new Error("useUploadHistory must be used within UploadHistoryProvider");
  return ctx;
}
